import * as Yup from "yup";
import * as httpService from "../services/httpService.v2";
import * as localStorageKeys from "../constants/localStorageKeys";

import { Form, Formik, FormikProps } from "formik";
import {
  Grid,
  Paper,
  TextField,
  Theme,
  Typography,
  createStyles,
  makeStyles,
} from "@material-ui/core";
import React, { useContext, useState } from "react";

import Button from "./Button";
import { LangContext } from "../context/LangContext";
import LoadingIndicator from "./LoadingIndicator";
import { UserContext } from "../context/UserContext";
import { clearLocalStorage } from "../utils";
import translations from "../assets/json/translations.json";
import { useHistory } from "react-router-dom";
import useNotifier from "../hooks/useNotifier";

export interface IFormValues {
  phoneNumber: string;
}

type IFormik = FormikProps<IFormValues>;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      height: `calc(100vh - ${theme.dimensions.navbarHeight}px)`,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    paper: {
      flex: 1,
      padding: theme.spacing(3),
      margin: theme.spacing(3),
      textAlign: "center",
      maxWidth: theme.dimensions.boxWidth,
    },
    typographyTitle: {
      padding: theme.spacing(2),
      [theme.breakpoints.down("xs")]: {
        fontSize: 20,
      },
    },
    tabs: {
      width: "100%",
      padding: theme.spacing(1),
      paddingBottom: theme.spacing(2),
    },
    typographyError: {
      color: theme.palette.error.main,
    },
  })
);

const loginSchema = Yup.object().shape({
  phoneNumber: Yup.string()
    .matches(/^[0-9]{7}$/)
    .required(),
});

const Login: React.FC = () => {
  const [, setUserContext] = useContext(UserContext);
  const [lang] = useContext(LangContext);
  const classes = useStyles();
  const history = useHistory();
  const { notifyError } = useNotifier();

  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);

  if (loading) {
    return (
      <div className={classes.root}>
        <Grid container spacing={2} className={classes.paper}>
          <Grid item xs={12} sm={12}>
            <Typography variant="h4">
              {translations.authenticating[lang]}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12}>
            <LoadingIndicator />
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Formik
        enableReinitialize
        initialValues={{
          phoneNumber: "",
        }}
        onSubmit={onLogin}
        validationSchema={loginSchema}
      >
        {renderLoginForm}
      </Formik>
    </div>
  );

  function renderLoginForm(formik: IFormik) {
    const {
      values,
      errors,
      handleChange,
      handleBlur,
      handleSubmit,
      isSubmitting,
      submitCount,
    } = formik;

    return (
      <Form onSubmit={handleSubmit} style={{ margin: "auto 0" }}>
        <Paper elevation={2} className={classes.paper}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <Typography className={classes.typographyTitle} variant="h4">
                {translations.loginWithCertificates[lang]}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12}>
              <TextField
                fullWidth
                autoFocus
                autoComplete="off"
                id="phoneNumber"
                label={translations.phoneNumber[lang]}
                variant="outlined"
                inputProps={{
                  type: "text",
                  pattern: "[0-9]*",
                  maxLength: "7",
                }}
                value={values.phoneNumber}
                onChange={(e) => e.target.validity.valid && handleChange(e)}
                onBlur={handleBlur}
                error={!!errors.phoneNumber && !!submitCount}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Button fullWidth type="submit" disabled={isSubmitting}>
                {translations.logIn[lang]}
              </Button>
            </Grid>
            {showError && (
              <Grid item xs={12} sm={12}>
                <Typography variant="body1" className={classes.typographyError}>
                  {translations.loginFailed[lang]}
                </Typography>
              </Grid>
            )}
          </Grid>
          <Typography
            style={{
              textAlign: "end",
              paddingTop: "10px",
              fontSize: "0.8rem",
            }}
            color="textSecondary"
          >
          </Typography>
        </Paper>
      </Form>
    );
  }

  function onLogin(values: IFormValues) {
    setLoading(true);
    httpService
      .login(values.phoneNumber)
      .then((user: any) => {
        if (user.isOk) {
          setShowError(false);
          const extendedUser = {
            ...user.data,
          };
          setUserContext(extendedUser);
          localStorage.setItem(
            localStorageKeys.USER,
            JSON.stringify(extendedUser)
          );

          clearLocalStorage();
          history.push("/home");
        } else {
          notifyError(
            user.data.errorMessage || translations.noCancerAccess[lang]
          );
          setShowError(true);
          setLoading(false);
        }
      })
      .catch((e) => {
        setLoading(false);
      });
  }
};

export default Login;
